import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import StatsCard from '../components/shared/StatsCard';
import LoadingState from '../components/shared/LoadingState';
import CreateAndMarkAttendanceModal from '../components/convener/CreateAndMarkAttendanceModal';
import MemberManagement from '../components/convener/MemberManagement';
import MeetingActions from '../components/convener/MeetingActions';
import AttendanceModal from '../components/convener/AttendanceModal';
import { useMeetings } from '../hooks/useMeetings';
import { useAuth } from '../contexts/AuthContext';
import { MeetingService } from '../services/meetingService';
import { PDFReportGenerator } from '../utils/pdfReportGenerator';
import { Calendar, Users, CheckSquare, Plus, RefreshCw, MessageSquare, AlertTriangle, TrendingUp, UserCheck, UserX, FileText } from 'lucide-react';

export default function ConvenorPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'meetings' | 'members'>('dashboard');
  const [showCreateAndMarkModal, setShowCreateAndMarkModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedMeetingForAttendance, setSelectedMeetingForAttendance] = useState<any>(null);
  const [selectedMeetingTypeFilter, setSelectedMeetingTypeFilter] = useState<string>('');
  const { meetingTypes, instances, stats, loading, refresh } = useMeetings();

  const filteredInstances = selectedMeetingTypeFilter
    ? instances.filter(instance => instance.meeting_type_id === selectedMeetingTypeFilter)
    : instances;

  const handleAttendanceClick = (meeting: any) => {
    setSelectedMeetingForAttendance(meeting);
    setShowAttendanceModal(true);
  };

  const handleExportMeetingPDF = async (instance: any) => {
    const { data: records } = await MeetingService.getAttendanceForMeeting(instance.id);
    
    const presentRecords = records?.filter(r => r.status === 'Present').map((r: any) => ({
      name: r.members?.name || 'Unknown',
      phone: r.members?.phone
    })) || [];
    const absentRecords = records?.filter(r => r.status === 'Absent').map((r: any) => ({
      name: r.members?.name || 'Unknown',
      phone: r.members?.phone
    })) || [];

    PDFReportGenerator.generateSingleMeetingPDF({
      meetingTitle: instance.meeting_types?.name || 'Meeting',
      meetingType: instance.meeting_types?.name,
      date: instance.date,
      time: instance.time,
      convenerName: user?.name,
      presentRecords,
      absentRecords
    });
  };

  const handleShareMeetingWhatsApp = async (instance: any) => {
    const { data: records } = await MeetingService.getAttendanceForMeeting(instance.id);
    
    const total = records?.length || 0;
    const presentRecords = records?.filter(r => r.status === 'Present') || [];
    const absentRecords = records?.filter(r => r.status === 'Absent') || [];
    const presentCount = presentRecords.length;
    const absentCount = absentRecords.length;
    const percent = total > 0 ? ((presentCount / total) * 100).toFixed(1) : '0';

    let text = `📊 *MEETING ATTENDANCE SUMMARY*\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    text += `📌 *Meeting:* ${instance.meeting_types?.name || 'Meeting'}\n`;
    text += `📅 *Date:* ${instance.date}${instance.time ? `  🕒 *Time:* ${instance.time}` : ''}\n`;
    if (user?.name) text += `👤 *Convener:* ${user.name}\n`;
    text += `\n👥 *ATTENDANCE STATS:*\n`;
    text += `• Total Members: *${total}*\n`;
    text += `• ✅ Present: *${presentCount}* (${percent}%)\n`;
    text += `• ❌ Absent: *${absentCount}*\n`;

    if (absentRecords.length > 0) {
      text += `\n📝 *ABSENTEE LIST (${absentRecords.length}):*\n`;
      absentRecords.forEach((r: any, idx: number) => {
        const name = r.members?.name || 'Unknown';
        const phone = r.members?.phone ? ` (${r.members.phone})` : '';
        text += `${idx + 1}. ${name}${phone}\n`;
      });
    }

    text += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    text += `*State Meeting Tracker | SSF Kerala*`;

    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const sendWhatsAppReminderToMember = (name: string, phone: string) => {
    let text = `Salam ${name},\nReminder regarding SSF Kerala Meeting attendance. Please ensure your presence in upcoming meetings.\n\n*State Meeting Tracker | SSF Kerala*`;
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const phoneWithCountry = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;
    window.open(`https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading && activeTab === 'dashboard') {
    return (
      <Layout title="Convener Dashboard">
        <LoadingState title="Loading Convener Dashboard & Insights..." minHeight="min-h-[400px]" />
      </Layout>
    );
  }

  const navTabs = [
    { id: 'dashboard', label: 'Insights', icon: TrendingUp },
    { id: 'meetings', label: 'Meetings', icon: Calendar },
    { id: 'members', label: 'Members', icon: Users },
  ];

  return (
    <Layout 
      title="Convener Portal" 
      tabs={navTabs} 
      activeTab={activeTab} 
      onTabChange={(id: string) => setActiveTab(id as 'dashboard' | 'meetings' | 'members')}
    >
      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-8">
          {/* Convener Action Header (Hero Section) */}
          <div className="relative bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-6 sm:p-10 shadow-lg shadow-blue-500/20 overflow-hidden">
            {/* Decorative background blobs */}
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-20 w-40 h-40 bg-indigo-400 opacity-20 rounded-full blur-2xl"></div>
            
            <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              <div className="max-w-xl">
                <h2 className="text-2xl sm:text-3xl font-black text-white mb-2 tracking-tight">Welcome back, {user?.name}!</h2>
                <p className="text-blue-100 font-medium text-sm sm:text-base leading-relaxed">
                  Record attendance, organize your meeting schedules, and review member participation easily from your portal.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                <button
                  onClick={refresh}
                  className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-3 sm:py-3.5 bg-white/10 hover:bg-white/20 text-white rounded-xl backdrop-blur-md border border-white/20 font-bold text-sm transition-all shadow-sm"
                  title="Refresh Insights"
                >
                  <RefreshCw size={18} />
                  <span className="sm:hidden">Refresh Data</span>
                </button>
                <button 
                  onClick={() => setShowCreateAndMarkModal(true)}
                  className="w-full sm:w-auto flex items-center justify-center space-x-2 px-6 py-3 sm:py-3.5 bg-white text-blue-700 rounded-xl hover:bg-blue-50 font-black text-sm shadow-xl shadow-blue-900/20 transition-all hover:-translate-y-1"
                >
                  <Plus size={20} className="stroke-[3px]" />
                  <span>New Meeting</span>
                </button>
              </div>
            </div>
          </div>

          {/* Primary KPI Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <StatsCard title="Total Meetings" value={stats?.totalMeetings || 0} icon={Calendar} color="blue" subtext="All time instances" />
            <StatsCard title="Completed" value={`${stats?.completedMeetings || 0}`} icon={CheckSquare} color="green" subtext={`${stats?.completionRate || 0}% completion rate`} />
            <StatsCard title="Avg Attendance" value={`${stats?.overallAttendanceRate || 0}%`} icon={TrendingUp} color="purple" subtext="Across all meetings" />
            <StatsCard title="Total Members" value={stats?.totalMembers || 0} icon={Users} color="orange" subtext="Assigned to your types" />
          </div>

          {/* Member Participation Health Insights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-green-800 uppercase tracking-wide">Regular Attendees (≥80%)</div>
                <div className="text-2xl font-bold text-green-900 mt-1">{stats?.highAttendanceCount || 0} Members</div>
                <div className="text-[11px] text-green-700 mt-0.5">Consistently attending meetings</div>
              </div>
              <div className="p-3 bg-green-500 text-white rounded-xl shadow-xs">
                <UserCheck size={22} />
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide">At Risk (&lt;50%)</div>
                <div className="text-2xl font-bold text-amber-900 mt-1">{stats?.atRiskCount || 0} Members</div>
                <div className="text-[11px] text-amber-700 mt-0.5">Need follow-up & encouragement</div>
              </div>
              <div className="p-3 bg-amber-500 text-white rounded-xl shadow-xs">
                <AlertTriangle size={22} />
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold text-red-800 uppercase tracking-wide">Zero Attendance</div>
                <div className="text-2xl font-bold text-red-900 mt-1">{stats?.zeroAttendanceCount || 0} Members</div>
                <div className="text-[11px] text-red-700 mt-0.5">Have not attended any meeting</div>
              </div>
              <div className="p-3 bg-red-500 text-white rounded-xl shadow-xs">
                <UserX size={22} />
              </div>
            </div>
          </div>

          {/* Assigned Meeting Types Performance Breakdown */}
          {stats?.meetingTypeBreakdown && stats.meetingTypeBreakdown.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-4">Meeting Type Attendance Insights</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.meetingTypeBreakdown.map((item: any) => (
                  <div key={item.id} className="p-4 border border-gray-200 rounded-xl bg-gray-50/50 hover:bg-gray-50 transition space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-bold text-gray-900 text-sm sm:text-base">{item.name}</div>
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 inline-block mt-1">
                          {item.level} Level
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500 font-medium">Attendance Rate</div>
                        <div className={`text-base font-extrabold ${
                          item.attendanceRate >= 75 ? 'text-green-600' :
                          item.attendanceRate >= 50 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {item.attendanceRate}%
                        </div>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          item.attendanceRate >= 75 ? 'bg-green-500' :
                          item.attendanceRate >= 50 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${item.attendanceRate}%` }}
                      ></div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500 font-medium pt-1">
                      <span>{item.meetingsCount} Meetings Held</span>
                      <span>{item.membersCount} Members</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Members Needing Follow-up Spotlight */}
          {stats?.atRiskMembers && stats.atRiskMembers.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-gray-900">Members Needing Follow-up</h2>
                  <p className="text-xs text-gray-500">Members with low attendance rate needing convener contact.</p>
                </div>
                <button
                  onClick={() => setActiveTab('members')}
                  className="text-xs font-bold text-blue-600 hover:text-blue-800"
                >
                  View All Members
                </button>
              </div>

              <div className="divide-y divide-gray-100">
                {stats.atRiskMembers.map((member: any) => (
                  <div key={member.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="h-9 w-9 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-bold text-xs shrink-0">
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 text-xs sm:text-sm truncate">{member.name}</div>
                        <div className="text-[11px] text-gray-500">{member.meeting_types?.name} • {member.phone}</div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3 shrink-0">
                      <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                        {member.rate}% Attended
                      </span>
                      <button
                        onClick={() => sendWhatsAppReminderToMember(member.name, member.phone)}
                        className="p-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-xs font-semibold flex items-center space-x-1 transition"
                        title="Send WhatsApp Reminder"
                      >
                        <MessageSquare size={14} />
                        <span className="hidden sm:inline">Remind</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Meetings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Recent Meetings</h2>
              <button
                onClick={() => setActiveTab('meetings')}
                className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-bold"
              >
                View All ({instances.length})
              </button>
            </div>
            {instances.length > 0 ? (
              <div className="space-y-3">
                {instances.slice(0, 5).map((instance) => (
                  <div key={instance.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition gap-2">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="font-bold text-gray-900 text-sm">{instance.meeting_types?.name}</p>
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {instance.meeting_types?.level}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          instance.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          instance.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {instance.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{instance.date} {instance.time && `• ${instance.time}`}</p>
                    </div>

                    <div className="flex items-center space-x-2 self-end sm:self-center">
                      <button
                        onClick={() => handleAttendanceClick(instance)}
                        className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-semibold rounded-lg transition"
                      >
                        Manage Attendance
                      </button>
                      <button
                        onClick={() => handleExportMeetingPDF(instance)}
                        className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                        title="Export PDF Report"
                      >
                        <FileText size={16} />
                      </button>
                      <button
                        onClick={() => handleShareMeetingWhatsApp(instance)}
                        className="p-1.5 text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition"
                        title="Share WhatsApp Summary"
                      >
                        <MessageSquare size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No meetings yet. Create your first meeting to get started!
              </div>
            )}
          </div>
        </div>
      )}

      {/* Meetings Tab */}
      {activeTab === 'meetings' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <h2 className="text-lg font-bold text-gray-900">My Meetings</h2>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <select
                value={selectedMeetingTypeFilter}
                onChange={(e) => setSelectedMeetingTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
              >
                <option value="">All Meeting Types</option>
                {meetingTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} - {type.level}
                  </option>
                ))}
              </select>
              <button 
                onClick={() => setShowCreateAndMarkModal(true)}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-xs sm:text-sm font-semibold"
              >
                <Plus size={16} />
                <span>Create Meeting</span>
              </button>
            </div>
          </div>
          {filteredInstances.length > 0 ? (
            <div className="space-y-3">
              {filteredInstances.map((instance) => (
                <div key={instance.id} className="flex flex-col p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
                  <div className="flex-1 mb-3">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900">{instance.meeting_types?.name}</h3>
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {instance.meeting_types?.level}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        instance.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        instance.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {instance.status}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2">{instance.date} {instance.time && `• ${instance.time}`}</p>
                    
                    {/* Attendance Summary */}
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Users size={14} />
                        <span>Attendance: {instance.attendance_summary?.present || 0}/{instance.attendance_summary?.total || 0}</span>
                      </div>
                      {instance.attendance_summary && instance.attendance_summary.total > 0 && (
                        <div className="flex items-center space-x-1">
                          <div className={`w-2 h-2 rounded-full ${
                            (instance.attendance_summary.present / instance.attendance_summary.total) >= 0.8 
                              ? 'bg-green-500' 
                              : (instance.attendance_summary.present / instance.attendance_summary.total) >= 0.6 
                                ? 'bg-yellow-500' 
                                : 'bg-red-500'
                          }`}></div>
                          <span className="font-semibold">{instance.attendance_summary.percentage}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-between pt-2 border-t border-gray-100">
                    <MeetingActions 
                      meeting={instance} 
                      onUpdate={refresh}
                      onAttendanceClick={() => handleAttendanceClick(instance)}
                      onWhatsAppShare={() => handleShareMeetingWhatsApp(instance)}
                      onPDFExport={() => handleExportMeetingPDF(instance)}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8 text-xs sm:text-sm">
              {selectedMeetingTypeFilter 
                ? 'No meetings found for this meeting type. Try a different filter or create a new meeting.'
                : 'No meetings found. Create your first meeting!'
              }
            </div>
          )}
        </div>
      )}

      {/* Members Tab */}
      {activeTab === 'members' && <MemberManagement />}

      {/* Create and Mark Attendance Modal */}
      <CreateAndMarkAttendanceModal 
        isOpen={showCreateAndMarkModal} 
        onClose={() => setShowCreateAndMarkModal(false)} 
        refresh={refresh}
      />

      {/* Attendance Modal */}
      {selectedMeetingForAttendance && (
        <AttendanceModal
          isOpen={showAttendanceModal}
          onClose={() => {
            setShowAttendanceModal(false);
            setSelectedMeetingForAttendance(null);
            refresh();
          }}
          meetingId={selectedMeetingForAttendance.id}
          meetingName={selectedMeetingForAttendance.meeting_types?.name}
          meetingDate={selectedMeetingForAttendance.date}
          meetingTime={selectedMeetingForAttendance.time}
        />
      )}
    </Layout>
  );
}
